import { pxToRem } from '@back-office/core';
import { Invitation } from '@back-office/http';
import { Button, Link } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import { isPast, parseJSON } from 'date-fns';
import { observer } from 'mobx-react';
import React, { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { EmailResendWaitTimeMap } from '../../common';
import { usePagination } from '../../hooks';
import { getDatetimeText } from '../../utils';
import GcpBox from '../gcp-box';
import GcpClientPagination from '../gcp-cilent-pagination';
import ButtonWithTimer from '../gcp-timer-button';
import GcpTypography from '../gcp-typography';

export const useInvitationStyles = makeStyles(
  (theme) => ({
    table: {
      minWidth: 850,
      tableLayout: 'fixed',
    },
    success_popup: {
      width: '100%',
      textAlign: 'center',
      padding: '16px',
      margin: '6px 0',
      borderRadius: '4px',
    },
    isd_code: {
      fontSize: '14px',
      height: '40px',
      background: '#E0E0E0',
      paddingTop: '10px',
      paddingLeft: '10px',
      paddingRight: '10px',
      border: '1px solid #BDBDBD',
      borderTopLeftRadius: '10px',
      borderBottomLeftRadius: '10px',
      borderTopRightRadius: '0px',
      borderBottomRightRadius: '0px',
      color: '#BDBDBD',
    },
    validation_message: {
      fontSize: '12px',
      color: '#828282',
      fontWeight: 400,
      textAlign: 'left',
    },
    headerCell: {
      borderTop: `1px solid rgba(172, 181, 189, 0.5)`,
      borderBottom: `1px solid rgba(172, 181, 189, 0.5)`,
      position: 'relative',
      padding: theme.spacing(1),
      '&:not(:last-child):after': {
        content: '"|"',
        color: theme.palette.common.hitGray,
        position: 'absolute',
        bottom: '36%',
        right: 0,
        height: '50%',
        width: '0px',
      },
      '& > div ': {
        margin: '0px',
      },
    },
    icon: {
      backgroundColor: 'rgba(0, 122, 255, 0.1)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(1.5),
      borderRadius: '32px',
    },
    role: {
      paddingTop: theme.spacing(0.5),
    },
    email: {
      color: theme.palette.common.azureRadiant,
      width: pxToRem(20),
      height: pxToRem(20),
    },
    icons: {
      width: pxToRem(18),
      height: pxToRem(18),
      '&.trash': {
        color: 'white',
        stroke: theme.palette.common.azureRadiant,
      },
    },
    secondary: {
      color: theme.palette.common.azureRadiant,
      border: `1px solid ${theme.palette.common.azureRadiant}`,
      boxSizing: 'border-box',
      borderRadius: '20px',
      '&:hover, &:focus': {
        background: 'rgba(0, 122, 255, 0.1)',
      },
    },
    warning: {
      color: 'white',
      '&:hover, &:focus': {
        background: 'white !important',
      },
    },
    close: {
      color: theme.palette.common.torchRed,
      width: pxToRem(32),
      height: pxToRem(32),
    },
    disabled: {
      color: theme.palette.common.hitGray,
    },
    cell: {
      padding: theme.spacing(1),
    },
    hidden: {
      visibility: 'hidden',
    },
    edited_phone: {
      '& > .MuiInputBase-formControl  > .MuiOutlinedInput-notchedOutline': {
        borderLeft: 'none',
        borderTopLeftRadius: '0px',
        borderBottomLeftRadius: '0px',
      },
    },
    top_align: {
      '&.MuiTableCell-root': {
        verticalAlign: 'top !important',
      },
    },
  }),
  { name: 'GcpInvitationAndUserTable' }
);

interface GcpInvitationTableProps {
  data: Invitation[];
  emailToTimerMap: EmailResendWaitTimeMap;
  handleOnClickResend: (invitation: Invitation) => void;
  handleOnClickRemove: (invitation: Invitation) => void;
}

const GcpInvitationTable: FC<GcpInvitationTableProps> = observer((props) => {
  const classes = useInvitationStyles();
  const { t } = useTranslation();
  const { handleChangePage, page, rowsPerPage } = usePagination();
  const { data, handleOnClickResend, handleOnClickRemove } = props;

  return (
    <TableContainer component="div">
      <Table aria-label="GCP Invitation Table" className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell
              align="center"
              className={classes.headerCell}
              width="32%"
            >
              <GcpBox ml={7}>
                <GcpTypography paint="hitGray" variant="body1">
                  {t('invite_new_user.email')}
                </GcpTypography>
              </GcpBox>
            </TableCell>
            <TableCell
              align="center"
              className={classes.headerCell}
              width="20%"
            >
              <GcpBox ml={7}>
                <GcpTypography paint="hitGray" variant="body1">
                  {t('invite_new_user.mobile_number')}
                </GcpTypography>
              </GcpBox>
            </TableCell>
            <TableCell
              align="center"
              className={classes.headerCell}
              width="12%"
            >
              <GcpTypography paint="hitGray" variant="body1">
                {t('invite_new_user.permission')}
              </GcpTypography>
            </TableCell>
            <TableCell align="left" className={classes.headerCell} width="33%">
              <GcpTypography
                paint="hitGray"
                style={{ marginLeft: '20px' }}
                variant="body1"
              >
                {t('invite_new_user.invited_at')}
              </GcpTypography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row) => (
              <TableRow key={row.email}>
                <TableCell component="th" scope="row">
                  <GcpBox display="flex">
                    <GcpBox className={classes.icon}>
                      <EmailOutlinedIcon className={classes.email} />
                    </GcpBox>
                    <GcpBox alignItems="center" display="flex" ml={1}>
                      <GcpTypography wordBreak="break-all">
                        {row.email}
                        <br />
                        {isPast(parseJSON(row.expiredAt)) && (
                          <>
                            <Link className={classes.disabled} underline="none">
                              {t('invite_new_user.invalid')}
                            </Link>
                            &nbsp;
                            <Link
                              color="secondary"
                              style={{ cursor: 'pointer' }}
                              underline="always"
                              onClick={() => handleOnClickResend(row)}
                            >
                              {t('invite_new_user.new')}
                            </Link>
                          </>
                        )}
                      </GcpTypography>
                    </GcpBox>
                  </GcpBox>
                </TableCell>
                <TableCell align="left">
                  <GcpBox display="flex" justifyContent="center">
                    <GcpTypography variant="body1">{row.phone}</GcpTypography>
                  </GcpBox>
                </TableCell>
                <TableCell align="left">
                  <GcpBox display="flex" justifyContent="center">
                    <GcpTypography variant="body1">{row.role}</GcpTypography>
                  </GcpBox>
                </TableCell>
                <TableCell align="left">
                  <GcpBox
                    alignItems="center"
                    display="flex"
                    justifyContent="flex-start"
                  >
                    <GcpBox width="50%">
                      <GcpTypography variant="body1">
                        {getDatetimeText(row.createdAt, 'default')}
                      </GcpTypography>
                    </GcpBox>
                    &nbsp;
                    <GcpBox width="20%">
                      <Button
                        className={classes.secondary}
                        variant="outlined"
                        onClick={() => handleOnClickRemove(row)}
                      >
                        <DeleteOutlineOutlinedIcon className={classes.icons} />
                      </Button>
                    </GcpBox>
                    &nbsp;
                    <GcpBox display="flex" width="25%">
                      <ButtonWithTimer
                        email={row.email}
                        enableTime={props.emailToTimerMap[row.email]}
                        handleOnClickResend={props.handleOnClickResend}
                        row={row}
                      />
                    </GcpBox>
                  </GcpBox>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {data.length === 0 && (
        <GcpBox isFullWidth display="flex" mt={2}>
          <GcpTypography paint="hitGray" variant="body1">
            {t('invite_new_user.no_active_invites')}
          </GcpTypography>
        </GcpBox>
      )}
      {data.length !== 0 && (
        <GcpClientPagination
          handleChangePage={handleChangePage}
          page={page}
          rowsPerPage={rowsPerPage}
          total={data.length}
        />
      )}
    </TableContainer>
  );
});

export default memo(GcpInvitationTable);
